// Hover preview for a calendar booking chip — shows a skeleton for a minimum
// 800ms, then the appointment details. Data already comes with the chip, so
// the skeleton is a deliberate reveal, not a real fetch.

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Clock, Mail, StickyNote, Hash, CalendarDays } from 'lucide-react';
import clsx from 'clsx';
import { APPT_STATUS_META, to12h, type ApptCalendarBooking } from '@/services/appointments.service';

const CARD_W = 280;
const GAP = 8;

const HERO: Record<string, string> = {
  PENDING: 'from-amber-500 to-amber-600',
  CONFIRMED: 'from-blue-500 to-blue-600',
  COMPLETED: 'from-emerald-500 to-emerald-600',
  CANCELLED: 'from-rose-500 to-rose-600',
  NO_SHOW: 'from-slate-500 to-slate-600',
};

const Bar = ({ w, dark }: { w: string; dark?: boolean }) => (
  <span className={clsx('block h-2.5 rounded animate-pulse', dark ? 'bg-white/40' : 'bg-surface-alt')} style={{ width: w }} />
);

const Line = ({ icon: Icon, label, children }: { icon: typeof Clock; label: string; children: React.ReactNode }) => (
  <div className="flex items-baseline gap-2.5 py-[3px]">
    <span className="flex items-center gap-1.5 text-[10.5px] text-text-muted w-[62px] shrink-0"><Icon size={11} className="opacity-70" /> {label}</span>
    <span className="min-w-0 flex-1 text-[12px] font-medium text-text-primary break-words">{children}</span>
  </div>
);

const BookingHoverCard = ({ booking, children }: { booking: ApptCalendarBooking; children: React.ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skeletonTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => () => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (skeletonTimer.current) clearTimeout(skeletonTimer.current);
  }, []);

  const show = () => {
    openTimer.current = setTimeout(() => {
      setOpen(true);
      setLoading(true);
      skeletonTimer.current = setTimeout(() => setLoading(false), 800);
    }, 120);
  };
  const hide = () => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (skeletonTimer.current) clearTimeout(skeletonTimer.current);
    setOpen(false);
    setPos(null);
  };

  // Portal + fixed positioning with viewport collision handling: prefer the
  // right side of the chip, flip left when there's no room, clamp vertically.
  // Re-measures after render (skeleton → content changes the card height).
  useLayoutEffect(() => {
    if (!open) return;
    const trigger = wrapRef.current;
    const card = cardRef.current;
    if (!trigger || !card) return;
    const r = trigger.getBoundingClientRect();
    const cardH = card.offsetHeight;
    let left = r.right + GAP;
    if (left + CARD_W > window.innerWidth - GAP) left = r.left - CARD_W - GAP;
    left = Math.max(GAP, left);
    const top = Math.max(GAP, Math.min(r.top, window.innerHeight - cardH - GAP));
    setPos((p) => (p && p.top === top && p.left === left ? p : { top, left }));
  }, [open, loading]);

  const meta = APPT_STATUS_META[booking.status];
  const niceDate = new Date(`${booking.date}T00:00:00`).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  const initials = (booking.customerName || '?').trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('') || '?';

  return (
    <div ref={wrapRef} onMouseEnter={show} onMouseLeave={hide}>
      {children}
      {open && createPortal(
        <div
          ref={cardRef}
          style={{ position: 'fixed', top: pos?.top ?? -9999, left: pos?.left ?? -9999, width: CARD_W }}
          className="z-[100] rounded-2xl overflow-hidden shadow-elevated border border-border bg-surface pointer-events-none"
        >
          {/* status-colored hero */}
          <div className={clsx('relative px-4 py-3 bg-gradient-to-br text-white', HERO[booking.status] ?? HERO.PENDING)}>
            <div className="text-[9px] font-bold uppercase tracking-[.14em] text-white/85 flex items-center gap-1.5 mb-1">
              <span className="w-1.5 h-1.5 rounded-full bg-white/90" /> {meta?.label ?? booking.status}
            </div>
            {loading ? <Bar w="65%" dark /> : (
              <div className="text-[14px] font-bold leading-tight truncate">{booking.serviceName}</div>
            )}
            <div className="text-[10.5px] text-white/75 inline-flex items-center gap-1 mt-1"><Hash size={9} />{booking.bookingNo}</div>
          </div>

          {/* body */}
          <div className="px-4 py-3">
            {loading ? (
              <div className="flex flex-col gap-3 py-1">
                <Bar w="90%" /><Bar w="72%" /><Bar w="58%" /><Bar w="80%" />
              </div>
            ) : (
              <>
                {/* customer strip */}
                <div className="flex items-center gap-2.5 pb-2.5 mb-1 border-b border-border/60">
                  <span className="w-8 h-8 rounded-full bg-primary-soft text-primary text-[11px] font-bold flex items-center justify-center shrink-0">{initials}</span>
                  <div className="min-w-0">
                    <div className="text-[12.5px] font-semibold text-text-primary truncate">{booking.customerName || 'Walk-in'}</div>
                    {booking.customerPhone && <div className="text-[11px] text-text-muted truncate">+{booking.customerPhone.replace(/^\+/, '')}</div>}
                  </div>
                </div>
                <Line icon={CalendarDays} label="When">{niceDate}</Line>
                <Line icon={Clock} label="Time">{to12h(booking.start)} – {to12h(booking.end)}</Line>
                {booking.customerEmail && <Line icon={Mail} label="Email">{booking.customerEmail}</Line>}
                {booking.notes && <Line icon={StickyNote} label="Notes">{booking.notes}</Line>}
                <div className="text-[10px] text-text-muted mt-2 pt-2 border-t border-border/60 text-center">Click to view full details</div>
              </>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default BookingHoverCard;
